// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/Purchase.tsx
//
// تغییرات این نسخه:
// - 🆕 سیستم هشدار کسری در ویرایش: وقتی کاربر مقدار را کاهش داد
//   و این خرید از یک بار خودکار آمده، هشدار + درخواست توضیحات + 
//   ثبت خودکار در customs_shortages
// - بقیه عملکرد دست نخورده
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, vendorsAPI, warehouseAPI, trackingAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, X, Printer, Search, Eye, Edit2, AlertTriangle } from "lucide-react";
import JournalFilter, { emptyFilter, applyFilter, JournalFilterValues, exportToExcel } from "./JournalFilter";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

interface Item {
  product_id: string; product_name: string; unit: string;
  quantity: number; warehouse_id: string; warehouse_name: string;
  unit_price: number; total_price: number;
}

interface ProductOption {
  value: string;
  label: string;
  product_id: number;
  product_name: string;
  warehouse_id: number;
  warehouse_name: string;
  unit: string;
  stock: number;
  item_type: "product" | "service";
}

// 🆕 ساختار کسری شناسایی شده
interface DetectedShortage {
  shipment_item_id: number;
  product_id: number;
  product_name: string;
  original_qty: number;
  actual_qty: number;
  shortage_qty: number;
}

export default function Purchase() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [list, setList]             = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editItem, setEditItem]     = useState<any>(null);
  const [viewItem, setViewItem]     = useState<any>(null);
  const [vendors, setVendors]       = useState<any[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [filter, setFilter]         = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]           = useState({ text: "", ok: true });
  const [statusFilter, setStatusFilter] = useState<"all"|"pending"|"confirmed">("all");

  // 🆕 state برای مدیریت هشدار کسری
  const [shortageWarning, setShortageWarning] = useState<{
    shortages: DetectedShortage[];
    shipmentInfo: { id: number; serial: string };
    purchaseId: number;
  } | null>(null);
  const [shortageDescription, setShortageDescription] = useState("");

  const [form, setForm] = useState({
    date: todayGregorian(), vendor_id: "", vendor_currency: "USD",
    discount: "0", notes: "", attachment: "", truck_plate: "", waybill_number: "",
  });
  const [items, setItems] = useState<Item[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, v, pr, w, u, sv] = await Promise.all([
      journalAPI.listPurchases(),
      vendorsAPI.list(),
      warehouseAPI.productsList(),
      warehouseAPI.warehousesList(),
      warehouseAPI.unitsList(),
      warehouseAPI.servicesList(),
    ]);

    if (p.success) setList(p.purchases || []);
    if (v.success) setVendors(v.vendors || []);

    const opts: ProductOption[] = [];

    if (pr.success && w.success) {
      const products = pr.products || [];
      const warehouses = w.warehouses || [];
      const units = u.success ? (u.units || []) : [];

      products.forEach((prod: any) => {
        const unit = units.find((u: any) => u.id === prod.unit_id)?.name || "";
        const stockList: any[] = prod.stock_by_warehouse || [];
        const registeredWhIds = stockList.filter((s: any) => Number(s.quantity) !== 0).map((s: any) => s.warehouse_id);
        const sorted = [...warehouses].sort((a: any, b: any) => {
          const aHas = registeredWhIds.includes(a.id) ? 0 : 1;
          const bHas = registeredWhIds.includes(b.id) ? 0 : 1;
          return aHas - bHas;
        });
        sorted.forEach((wh: any) => {
          const s = stockList.find((s: any) => s.warehouse_id === wh.id);
          const qty = s ? Number(s.quantity) : 0;
          const stockLabel = qty !== 0 ? ` (${qty.toLocaleString()})` : "";
          opts.push({
            value: `${prod.id}|${wh.id}`,
            label: `📦 ${prod.name} — ${wh.name}${stockLabel}`,
            product_id: prod.id,
            product_name: prod.name,
            warehouse_id: wh.id,
            warehouse_name: wh.name,
            unit,
            stock: qty,
            item_type: "product",
          });
        });
      });
    }

    if (sv.success) {
      const services = sv.services || [];
      services.forEach((svc: any) => {
        opts.push({
          value: `svc_${svc.id}`,
          label: `🔧 ${svc.name}`,
          product_id: svc.id,
          product_name: svc.name,
          warehouse_id: 0,
          warehouse_name: "",
          unit: "",
          stock: 0,
          item_type: "service",
        });
      });
    }

    setProductOptions(opts);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const addItem = () =>
    setItems(p => [...p, { product_id: "", product_name: "", unit: "", quantity: 1, warehouse_id: "", warehouse_name: "", unit_price: 0, total_price: 0 }]);

  const removeItem = (i: number) => setItems(p => p.filter((_, idx) => idx !== i));

  const updateItem = (i: number, key: keyof Item, val: any) => {
    setItems(p => p.map((item, idx) => {
      if (idx !== i) return item;
      const u = { ...item, [key]: val };

      if (key === "product_id") {
        const opt = productOptions.find(o => o.value === val);
        if (opt) {
          u.product_name   = opt.product_name;
          u.warehouse_id   = opt.item_type === "service" ? "" : String(opt.warehouse_id);
          u.warehouse_name = opt.item_type === "service" ? "خدمت" : opt.warehouse_name;
          u.unit           = opt.unit;
        }
      }

      if (key === "quantity" || key === "unit_price") {
        const q = key === "quantity" ? Number(val) : u.quantity;
        const price = key === "unit_price" ? Number(val) : u.unit_price;
        u.total_price = q * price;
      }
      return u;
    }));
  };

  const totalAmount = items.reduce((s, i) => s + i.total_price, 0);
  const netAmount   = totalAmount - (parseFloat(form.discount || "0") || 0);

  const buildSubmitPayload = (statusValue: "pending"|"confirmed") => {
    const vendor = vendors.find((v: any) => String(v.id) === form.vendor_id);
    return {
      date: form.date,
      vendor_id: parseInt(form.vendor_id),
      vendor_name: `${vendor?.first_name || ""} ${vendor?.last_name || ""} ${vendor?.company_name || ""}`.trim(),
      vendor_currency: vendor?.currency || form.vendor_currency,
      discount: parseFloat(form.discount || "0"),
      items: items.map(i => {
        const isService = i.product_id.startsWith("svc_");
        const productId = isService
          ? parseInt(i.product_id.replace("svc_", ""))
          : (i.product_id ? parseInt(i.product_id.split("|")[0]) : null);
        return {
          product_id:     productId,
          product_name:   i.product_name,
          unit:           i.unit,
          quantity:       i.quantity,
          warehouse_id:   (!isService && i.warehouse_id) ? parseInt(i.warehouse_id) : null,
          warehouse_name: i.warehouse_name,
          unit_price:     i.unit_price,
          total_price:    i.total_price,
          item_type:      isService ? "service" : "product",
        };
      }),
      notes: form.notes,
      attachment: form.attachment,
      truck_plate: form.truck_plate, // 🆕 شماره موتر
      waybill_number: form.waybill_number, // 🆕 شماره بارنامه
      status: statusValue,
    };
  };

  const validateForm = () => {
    if (!form.vendor_id) { showToast("فروشنده الزامی است", false); return false; }
    if (items.length === 0) { showToast("حداقل یک آیتم اضافه کنید", false); return false; }
    if (items.some(i => !i.product_name || i.quantity <= 0)) { showToast("اطلاعات آیتم‌ها ناقص است", false); return false; }
    return true;
  };

  const resetForm = () => {
    setForm({ date: todayGregorian(), vendor_id: "", vendor_currency: "USD", discount: "0", notes: "", attachment: "", truck_plate: "", waybill_number: "" });
    setItems([]);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const res = await journalAPI.addPurchase(buildSubmitPayload("confirmed"));
    if (res.success) {
      showToast(`خرید ${res.serial} با موفقیت ثبت شد`);
      setShowForm(false);
      resetForm();
      load();
    } else {
      showToast(res.message || "خطا در ثبت خرید", false);
    }
  };

  const handleSubmitPending = async () => {
    if (!validateForm()) return;
    const res = await journalAPI.addPurchase(buildSubmitPayload("pending"));
    if (res.success) {
      showToast(`خرید ${res.serial} در انتظار تأیید ذخیره شد`);
      setShowForm(false);
      resetForm();
      load();
    } else {
      showToast(res.message || "خطا در ثبت خرید", false);
    }
  };

  const handleConfirm = async (id: number, serial: string) => {
    if (!window.confirm(`تأیید نهایی خرید ${serial}؟\nموجودی گدام، حساب مصرفی و بیلانس فروشنده به‌روزرسانی می‌شود.`)) return;
    const res = await journalAPI.confirmPurchase(id);
    if (res.success) { showToast("خرید تأیید شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن خرید ${serial} به حالت در انتظار؟\nتأثیرات آن لغو می‌شود.`)) return;
    const res = await journalAPI.revertPurchase(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const openEdit = (p: any) => {
    setEditItem({
      id: p.id,
      date: p.date,
      vendor_id: String(p.vendor_id),
      vendor_currency: p.vendor_currency,
      discount: String(p.discount || "0"),
      notes: p.notes || "",
      attachment: p.attachment || "",
      truck_plate: p.truck_plate || "", // 🆕 شماره موتر
      waybill_number: p.waybill_number || "", // 🆕 شماره بارنامه
      status: p.status || "confirmed",
      items: (p.items || []).map((it: any) => ({
        product_id: `${it.product_id}|${it.warehouse_id}`,
        product_name: it.product_name,
        unit: it.unit,
        quantity: Number(it.quantity),
        warehouse_id: String(it.warehouse_id),
        warehouse_name: it.warehouse_name,
        unit_price: Number(it.unit_price),
        total_price: Number(it.total_price),
      })),
    });
  };

  // 🆕 تابع: تشخیص کسری در ویرایش (اگر این خرید به یک بار مرتبط است)
  // منطق: مجموع کل تعداد بار را با مجموع کل تعداد خرید مقایسه می‌کنیم
  // اگر مجموع خرید کمتر از مجموع بار شد → کسری
  const detectShortages = async (purchaseId: number, currentItems: any[]): Promise<{ shortages: DetectedShortage[]; shipmentInfo: { id: number; serial: string } | null }> => {
    const r = await trackingAPI.shipmentItemsForPurchase(purchaseId);
    if (!r.success || !r.shipment) return { shortages: [], shipmentInfo: null };
    
    const shipmentItems = r.items || [];
    if (shipmentItems.length === 0) return { shortages: [], shipmentInfo: null };
    
    // محاسبه مجموع کل بار
    const totalShipment = shipmentItems.reduce((sum: number, it: any) => sum + Number(it.quantity || 0), 0);
    
    // محاسبه مجموع کل خرید
    const totalPurchase = currentItems.reduce((sum: number, it: any) => sum + Number(it.quantity || 0), 0);
    
    const detected: DetectedShortage[] = [];
    
    // اگر مجموع خرید کمتر از مجموع بار است → کسری وجود دارد
    // tolerance کوچک برای جلوگیری از خطای floating-point
    if (totalShipment - totalPurchase > 0.001) {
      // یک رکورد کسری برای کل بار ثبت می‌کنیم
      // (اگر بار چند جنس داشت، اولین قلم را به‌عنوان نماینده استفاده می‌کنیم
      //  ولی مقدار کسری همان مقدار کلی است)
      const firstItem = shipmentItems[0];
      detected.push({
        shipment_item_id: firstItem.id,
        product_id: firstItem.product_id,
        product_name: firstItem.product_name,
        original_qty: totalShipment,
        actual_qty: totalPurchase,
        shortage_qty: totalShipment - totalPurchase,
      });
    }
    
    return { 
      shortages: detected, 
      shipmentInfo: r.shipment ? { id: r.shipment.id, serial: r.shipment.serial } : null 
    };
  };

  const handleEdit = async () => {
    if (!editItem.vendor_id) { showToast("فروشنده الزامی است", false); return; }
    if (!editItem.items || editItem.items.length === 0) { showToast("حداقل یک آیتم اضافه کنید", false); return; }
    
    // 🆕 قبل از ذخیره: چک کسری
    const detection = await detectShortages(editItem.id, editItem.items);
    if (detection.shortages.length > 0 && detection.shipmentInfo) {
      // هشدار به کاربر و درخواست توضیحات
      setShortageWarning({
        shortages: detection.shortages,
        shipmentInfo: detection.shipmentInfo,
        purchaseId: editItem.id,
      });
      setShortageDescription("");
      return;
    }
    
    // اگر کسری نبود، ذخیره عادی
    await saveEditedPurchase();
  };

  // 🆕 ذخیره خرید بعد از تأیید کسری
  const saveEditedPurchase = async () => {
    const vendor = vendors.find((v: any) => String(v.id) === editItem.vendor_id);
    const res = await journalAPI.updatePurchase({
      id: editItem.id,
      date: editItem.date,
      vendor_id: parseInt(editItem.vendor_id),
      vendor_name: `${vendor?.first_name || ""} ${vendor?.last_name || ""} ${vendor?.company_name || ""}`.trim(),
      vendor_currency: vendor?.currency || editItem.vendor_currency,
      discount: parseFloat(editItem.discount || "0"),
      items: editItem.items.map((i: any) => ({
        product_id:     i.product_id ? parseInt(String(i.product_id).split("|")[0]) : null,
        product_name:   i.product_name,
        unit:           i.unit,
        quantity:       i.quantity,
        warehouse_id:   i.warehouse_id ? parseInt(i.warehouse_id) : null,
        warehouse_name: i.warehouse_name,
        unit_price:     i.unit_price,
        total_price:    i.total_price,
      })),
      notes: editItem.notes,
      attachment: editItem.attachment,
      truck_plate: editItem.truck_plate || "", // 🆕 شماره موتر
      waybill_number: editItem.waybill_number || "", // 🆕 شماره بارنامه
    });
    if (res.success) {
      showToast("خرید ویرایش شد");
      setEditItem(null);
      setShortageWarning(null);
      setShortageDescription("");
      load();
    } else showToast(res.message || "خطا", false);
  };

  // 🆕 تأیید کسری: ثبت در tracking + ذخیره خرید
  const confirmShortageAndSave = async () => {
    if (!shortageWarning) return;
    if (!shortageDescription.trim()) {
      showToast("توضیح کسری الزامی است", false);
      return;
    }
    
    // ثبت همه کسری‌ها در tracking
    for (const sh of shortageWarning.shortages) {
      await trackingAPI.shortageAdd({
        shipment_id: shortageWarning.shipmentInfo.id,
        shipment_item_id: sh.shipment_item_id,
        product_id: sh.product_id,
        product_name: sh.product_name,
        original_qty: sh.original_qty,
        actual_qty: sh.actual_qty,
        description: shortageDescription,
        purchase_id: shortageWarning.purchaseId,
      });
    }
    
    // سپس ذخیره خرید
    await saveEditedPurchase();
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف خرید ${serial}؟`)) return;
    const res = await journalAPI.deletePurchase(id);
    if (res.success) { showToast("خرید حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const filtered = applyFilter(list, filter, {
    searchFields: ["serial", "vendor_name", "notes"],
    dateField: "date",
    amountField: "net_amount",
    currencyField: "vendor_currency",
    counterpartyField: "vendor_id",
  }).filter((p: any) => {
    const st = p.status || "confirmed";
    if (statusFilter === "all") return true;
    return st === statusFilter;
  });

  const pendingCount = list.filter((p:any) => (p.status||"confirmed") === "pending").length;

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>

      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      {/* Toolbar */}
      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در خریدها... (شماره سند، نام فروشنده، توضیحات)",
          showDateRange: true,
          showAmountRange: true,
          showCurrency: true,
          showCounterparty: true,
          counterpartyLabel: "فروشنده",
          counterpartyOptions: vendors.map((v: any) => ({
            value: String(v.id),
            label: `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim(),
          })),
        }}
        values={filter}
        onChange={setFilter}
        resultCount={filtered.length}
        totalCount={list.length}
        T={T}
        dark={dark}
            onExport={() => exportToExcel(filtered.map(p => ({
          "شماره سند": p.serial,
          "تاریخ": p.date,
          "فروشنده": p.vendor_name,
          "مبلغ کل": p.total_amount,
          "تخفیف": p.discount || 0,
          "خالص": p.net_amount,
          "ارز": p.vendor_currency,
          "توضیحات": p.notes || "",
        })), "خریدات")}
      exportLabel="دانلود Excel خریدات"
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs
          value={statusFilter}
          onChange={setStatusFilter}
          totalCount={list.length}
          pendingCount={pendingCount}
          T={T}
          dark={dark}
        />
        {canAdd && (
          <button onClick={() => { setShowForm(true); if (items.length === 0) addItem(); }} style={addBtnSt("#f59e0b")}>
            <Plus size={15} /> ثبت خرید جدید
          </button>
        )}
      </div>

      {/* List */}
      {loading ? <Loading T={T} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && <Empty T={T} text="هیچ خریدی ثبت نشده" />}
          {/* ردیف هدر — فقط وقتی لیست خالی نیست نمایش بده */}
          {filtered.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "130px 220px 1fr 110px 75px 1.2fr 160px 200px",
              gap: 12,
              padding: "10px 16px",
              borderRadius: 10,
              background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              border: `1px solid ${T.cardBorder}`,
              fontSize: 11,
              fontWeight: 700,
              color: T.subtext,
            }}>
              <div>شماره سند</div>
              <div>موتر / بارنامه</div>
              <div>فروشنده</div>
              <div>تاریخ</div>
              <div style={{ textAlign: "center" as const }}>آیتم‌ها</div>
              <div>شرح</div>
              <div style={{ textAlign: "left" as const }}>مبلغ خالص</div>
              <div style={{ textAlign: "center" as const }}>عملیات</div>
            </div>
          )}
          {filtered.map((p: any) => {
            const isPending = (p.status || "confirmed") === "pending";
            return (
            <div key={p.id} style={{
              ...rowSt(dark, T),
              ...(isPending ? pendingRowStyle(dark) : {}),
              display: "grid",
              gridTemplateColumns: "130px 220px 1fr 110px 75px 1.2fr 160px 200px",
              gap: 12,
              alignItems: "center",
            }}>
              {/* ستون 1: سریال + بدج در انتظار */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                <span style={badgeSt("#f59e0b")}>{p.serial}</span>
                {isPending && <PendingBadge />}
              </div>

              {/* ستون 2: شماره موتر و بارنامه (در یک ستون، عمودی) */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                {p.truck_plate && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 7,
                    background: "rgba(59,130,246,0.1)", color: "#3b82f6",
                    fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                    whiteSpace: "nowrap" as const,
                  }}>
                    🚚 {p.truck_plate}
                  </span>
                )}
                {p.waybill_number && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 7,
                    background: "rgba(14,165,233,0.1)", color: "#0ea5e9",
                    fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                    whiteSpace: "nowrap" as const,
                  }}>
                    📄 {p.waybill_number}
                  </span>
                )}
              </div>

              {/* ستون 3: نام فروشنده */}
              <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.vendor_name}
              </div>

              {/* ستون 4: تاریخ */}
              <div>
                <DualDate date={p.date} fontSize={12} color={T.subtext} />
              </div>

              {/* ستون 5: تعداد آیتم */}
              <div style={{ fontSize: 12, color: T.subtext, textAlign: "center" as const }}>
                {p.items?.length || 0} آیتم
              </div>

              {/* ستون 6: شرح */}
              <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" as const }}>
                {p.notes || "—"}
              </div>

              {/* ستون 7: مبلغ */}
              <div style={{ fontWeight: 800, color: "#f59e0b", fontSize: 14, textAlign: "left" as const, direction: "ltr" as const }}>
                {fmt(p.net_amount)} {p.vendor_currency}
              </div>

              {/* ستون 8: عملیات */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                <button onClick={() => setViewItem(p)} title="مشاهده" style={iconBtnSt("#8b5cf6")}><Eye size={13} /></button>
                {isPending && canEdit && (
                  <ConfirmIconButton onClick={() => handleConfirm(p.id, p.serial)} />
                )}
                {!isPending && canEdit && (
                  <RevertIconButton onClick={() => handleRevert(p.id, p.serial)} />
                )}
                {canEdit && (
                  <button onClick={() => openEdit(p)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>
                )}
                {canDelete && (
                  <button onClick={() => handleDelete(p.id, p.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* فرم ثبت خرید */}
      {showForm && (
        <Modal title="ثبت خرید جدید" onClose={() => setShowForm(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>فروشنده *</Lbl>
              <SearchableSelect
                options={vendors.map((v: any) => ({ value: String(v.id), label: `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim() + ` (${v.currency})` }))}
                value={form.vendor_id}
                onChange={v => {
                  const vn = vendors.find((x: any) => String(x.id) === v);
                  setForm(f => ({ ...f, vendor_id: v, vendor_currency: vn?.currency || "USD" }));
                }}
                placeholder="انتخاب فروشنده"
              />
            </div>
            <div>
              <Lbl T={T}>تاریخ</Lbl>
              <PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} />
            </div>
            <div>
              <Lbl T={T}>تخفیف</Lbl>
              <input value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                placeholder="0" style={inputSt(T)} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ fontSize: 13, color: T.text }}>آیتم‌های خرید</strong>
              <button onClick={addItem} style={addBtnSt("#f59e0b", true)}><Plus size={13} /> افزودن آیتم</button>
            </div>
            {items.map((item, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "4fr 90px 100px 110px 36px", gap: 8, marginBottom: 10, alignItems: "end", padding: "12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc", border: `1px solid ${T.border}` }}>
                <div>
                  {i === 0 && <Lbl T={T}>محصول / خدمت — گدام</Lbl>}
                  <SearchableSelect
                    options={productOptions.map(o => ({ value: o.value, label: o.label }))}
                    value={item.product_id}
                    onChange={v => updateItem(i, "product_id", v)}
                    placeholder="انتخاب محصول و گدام"
                  />
                  {item.warehouse_name && (
                    <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>
                      ✓ گدام: {item.warehouse_name} | واحد: {item.unit}
                    </div>
                  )}
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>واحد</Lbl>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.text, fontSize: 13, minHeight: 42 }}>
                    {item.unit || "—"}
                  </div>
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>تعداد</Lbl>}
                  <input type="number" value={item.quantity}
                    onChange={e => updateItem(i, "quantity", Number(e.target.value))}
                    style={inputSt(T)} />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>قیمت واحد</Lbl>}
                  <input type="number" value={item.unit_price}
                    onChange={e => updateItem(i, "unit_price", Number(e.target.value))}
                    style={inputSt(T)} />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>مجموع</Lbl>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(245,158,11,0.1)" : "#fffbeb", color: "#f59e0b", fontWeight: 700, fontSize: 13 }}>
                    {fmt(item.total_price)}
                  </div>
                </div>
                <div style={{ marginTop: i === 0 ? 22 : 0 }}>
                  <button onClick={() => removeItem(i)} style={{ ...iconBtnSt("#ef4444"), width: 36, height: 36 }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: dark ? "rgba(245,158,11,0.08)" : "#fffbeb", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div><span style={{ fontSize: 12, color: T.subtext }}>جمع کل: </span><strong style={{ color: T.text }}>{fmt(totalAmount)}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>تخفیف: </span><strong style={{ color: "#ef4444" }}>{fmt(parseFloat(form.discount || "0"))}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>خالص: </span><strong style={{ color: "#f59e0b", fontSize: 16 }}>{fmt(netAmount)} {form.vendor_currency}</strong></div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>توضیحات</Lbl>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} />
            </div>
            <div>
              <Lbl T={T}>🚚 شماره موتر</Lbl>
              <input value={form.truck_plate} onChange={e => setForm(f => ({ ...f, truck_plate: e.target.value }))} placeholder="مثلاً: 12345" style={{...inputSt(T), direction: "ltr" as const}} />
            </div>
            <div>
              <Lbl T={T}>📄 شماره بارنامه</Lbl>
              <input value={form.waybill_number} onChange={e => setForm(f => ({ ...f, waybill_number: e.target.value }))} placeholder="شماره بارنامه..." style={{...inputSt(T), direction: "ltr" as const}} />
            </div>
            <div>
              <Lbl T={T}>ضمیمه</Lbl>
              <input value={form.attachment} onChange={e => setForm(f => ({ ...f, attachment: e.target.value }))} placeholder="لینک یا نام فایل..." style={inputSt(T)} />
            </div>
          </div>

          <div style={{
            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center",
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`,
            flexWrap: "wrap"
          }}>
            <PendingHint T={T} />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={() => setShowForm(false)} T={T} />
              <PendingSubmitButton
                onClick={() => submit(handleSubmitPending)}
                loading={submitting}
                label="ذخیره خرید در انتظار"
              />
              <ConfirmSubmitButton
                onClick={() => submit(handleSubmit)}
                loading={submitting}
                color="#f59e0b"
                label="ثبت خرید"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* فرم ویرایش خرید */}
      {editItem && (
        <Modal title={`ویرایش خرید${editItem.status==="pending" ? " (در انتظار)" : ""}`} onClose={() => setEditItem(null)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>فروشنده *</Lbl>
              <SearchableSelect
                options={vendors.map((v: any) => ({ value: String(v.id), label: `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim() + ` (${v.currency})` }))}
                value={editItem.vendor_id}
                onChange={v => { const vn = vendors.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, vendor_id: v, vendor_currency: vn?.currency || "USD" })); }}
                placeholder="انتخاب فروشنده"
              />
            </div>
            <div>
              <Lbl T={T}>تاریخ</Lbl>
              <PersianDatePicker value={editItem.date} onChange={v => setEditItem((f: any) => ({ ...f, date: v }))} />
            </div>
            <div>
              <Lbl T={T}>تخفیف</Lbl>
              <input value={editItem.discount} onChange={e => setEditItem((f: any) => ({ ...f, discount: e.target.value }))} placeholder="0" style={inputSt(T)} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ fontSize: 13, color: T.text }}>آیتم‌ها</strong>
              <button onClick={() => setEditItem((f: any) => ({ ...f, items: [...f.items, { product_id: "", product_name: "", unit: "", quantity: 1, warehouse_id: "", warehouse_name: "", unit_price: 0, total_price: 0 }] }))} style={addBtnSt("#f59e0b", true)}><Plus size={13} /> افزودن آیتم</button>
            </div>
            {editItem.items.map((item: any, i: number) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "4fr 90px 100px 110px 36px", gap: 8, marginBottom: 10, alignItems: "end", padding: "12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc", border: `1px solid ${T.border}` }}>
                <div>
                  {i === 0 && <Lbl T={T}>محصول / گدام</Lbl>}
                  <SearchableSelect
                    options={productOptions.map(o => ({ value: o.value, label: o.label }))}
                    value={item.product_id}
                    onChange={v => {
                      const opt = productOptions.find(o => o.value === v);
                      setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? { ...it, product_id: v, product_name: opt?.product_name || "", warehouse_id: String(opt?.warehouse_id || ""), warehouse_name: opt?.warehouse_name || "", unit: opt?.unit || "", total_price: it.quantity * it.unit_price } : it) }));
                    }}
                    placeholder="انتخاب محصول و گدام"
                  />
                  {item.warehouse_name && <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>✓ گدام: {item.warehouse_name} | واحد: {item.unit}</div>}
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>واحد</Lbl>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.text, fontSize: 13, minHeight: 42 }}>{item.unit || "—"}</div>
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>تعداد</Lbl>}
                  <input type="number" value={item.quantity} onChange={e => setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? { ...it, quantity: Number(e.target.value), total_price: Number(e.target.value) * it.unit_price } : it) }))} style={inputSt(T)} />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>قیمت واحد</Lbl>}
                  <input type="number" value={item.unit_price} onChange={e => setEditItem((f: any) => ({ ...f, items: f.items.map((it: any, idx: number) => idx === i ? { ...it, unit_price: Number(e.target.value), total_price: it.quantity * Number(e.target.value) } : it) }))} style={inputSt(T)} />
                </div>
                <div>
                  {i === 0 && <Lbl T={T}>مجموع</Lbl>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(245,158,11,0.1)" : "#fffbeb", color: "#f59e0b", fontWeight: 700, fontSize: 13 }}>{fmt(item.total_price)}</div>
                </div>
                <div style={{ marginTop: i === 0 ? 22 : 0 }}>
                  <button onClick={() => setEditItem((f: any) => ({ ...f, items: f.items.filter((_: any, idx: number) => idx !== i) }))} style={{ ...iconBtnSt("#ef4444"), width: 36, height: 36 }}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div><Lbl T={T}>توضیحات</Lbl><input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>🚚 شماره موتر</Lbl><input value={editItem.truck_plate || ""} onChange={e => setEditItem((f: any) => ({ ...f, truck_plate: e.target.value }))} placeholder="مثلاً: 12345" style={{...inputSt(T), direction: "ltr" as const}} /></div>
            <div><Lbl T={T}>📄 شماره بارنامه</Lbl><input value={editItem.waybill_number || ""} onChange={e => setEditItem((f: any) => ({ ...f, waybill_number: e.target.value }))} placeholder="شماره بارنامه..." style={{...inputSt(T), direction: "ltr" as const}} /></div>
            <div><Lbl T={T}>ضمیمه</Lbl><input value={editItem.attachment} onChange={e => setEditItem((f: any) => ({ ...f, attachment: e.target.value }))} placeholder="لینک..." style={inputSt(T)} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <BtnCancel onClick={() => setEditItem(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#3b82f6" label="ذخیره ویرایش" />
          </div>
        </Modal>
      )}

      {/* 🆕 مودال هشدار کسری */}
      {shortageWarning && (
        <Modal title="⚠️ هشدار کسری" onClose={() => setShortageWarning(null)} dark={dark} T={T}>
          <div style={{
            padding: 16, borderRadius: 12, marginBottom: 16,
            background: dark ? "rgba(239,68,68,0.1)" : "#fef2f2",
            border: "1px solid rgba(239,68,68,0.3)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <AlertTriangle size={22} color="#ef4444" />
              <strong style={{ color: "#dc2626", fontSize: 15 }}>کسری در بار {shortageWarning.shipmentInfo.serial} شناسایی شد</strong>
            </div>
            <p style={{ fontSize: 12, color: T.text, marginBottom: 14, lineHeight: 1.7 }}>
              مقدار ثبت شده در بار با مقدار خرید فعلی اختلاف دارد. لطفاً جزئیات را بررسی کنید:
            </p>
            
            <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${T.cardBorder}` }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "2fr 100px 100px 100px",
                gap: 10, padding: "10px 14px",
                background: dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.05)",
                fontSize: 11, fontWeight: 700, color: T.subtext,
              }}>
                <span>جنس</span>
                <span style={{ textAlign: "center" }}>اصلی</span>
                <span style={{ textAlign: "center" }}>جدید</span>
                <span style={{ textAlign: "center" }}>کسری</span>
              </div>
              {shortageWarning.shortages.map((sh, i) => (
                <div key={i} style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 100px 100px 100px",
                  gap: 10, padding: "10px 14px",
                  borderTop: `1px solid ${T.cardBorder}`,
                  fontSize: 12, color: T.text,
                }}>
                  <span style={{ fontWeight: 700 }}>{sh.product_name}</span>
                  <span style={{ textAlign: "center" }}>{fmt(sh.original_qty)}</span>
                  <span style={{ textAlign: "center", color: "#3b82f6" }}>{fmt(sh.actual_qty)}</span>
                  <span style={{ textAlign: "center", color: "#ef4444", fontWeight: 800, fontSize: 13 }}>
                    {fmt(sh.shortage_qty)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Lbl T={T}>توضیح علت کسری *</Lbl>
            <textarea
              value={shortageDescription}
              onChange={e => setShortageDescription(e.target.value)}
              placeholder="مثلاً: کیسه‌های پاره، کم‌فروشی در گمرک، ضایعات در حمل، ..."
              rows={3}
              style={{
                ...inputSt(T),
                resize: "vertical",
                minHeight: 80,
              }}
            />
            <div style={{ fontSize: 11, color: T.subtext, marginTop: 6 }}>
              ℹ️ این توضیح در گزارش کسری بارها ثبت می‌شود تا قابل پیگیری باشد.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 14, borderTop: `1px solid ${T.cardBorder}` }}>
            <BtnCancel onClick={() => { setShortageWarning(null); setShortageDescription(""); }} T={T} />
            <button
              onClick={() => submit(confirmShortageAndSave)}
              disabled={submitting || !shortageDescription.trim()}
              style={{
                padding: "10px 24px", borderRadius: 10, border: "none",
                background: (!shortageDescription.trim() || submitting) ? "#9ca3af" : "linear-gradient(135deg,#ef4444,#dc2626)",
                color: "#fff", cursor: (!shortageDescription.trim() || submitting) ? "not-allowed" : "pointer",
                fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <AlertTriangle size={14} /> {submitting ? "در حال ثبت..." : "تأیید کسری و ذخیره"}
            </button>
          </div>
        </Modal>
      )}

      {/* مشاهده جزئیات + چاپ */}
      {viewItem && (
        <Modal title={`جزئیات خرید: ${viewItem.serial}`} onClose={() => setViewItem(null)} dark={dark} T={T}>
          <div id="print-purchase">
            <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: T.subtext }}>فروشنده: <strong style={{ color: T.text }}>{viewItem.vendor_name}</strong></span>
              <span style={{ fontSize: 12, color: T.subtext, display: "inline-flex", alignItems: "center", gap: 6 }}>تاریخ: <DualDate date={viewItem.date} fontSize={12} color={T.text} inline /></span>
              <span style={{ fontSize: 12, color: T.subtext }}>خالص: <strong style={{ color: "#f59e0b" }}>{fmt(viewItem.net_amount)} {viewItem.vendor_currency}</strong></span>
              {viewItem.notes && <span style={{ fontSize: 12, color: T.subtext }}>توضیح: <strong style={{ color: T.text }}>{viewItem.notes}</strong></span>}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: dark ? "rgba(255,255,255,0.05)" : "#f8fafc" }}>
                  {["محصول", "واحد", "تعداد", "گدام", "قیمت واحد", "مجموع"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: T.subtext, borderBottom: `1px solid ${T.cardBorder}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {viewItem.items?.map((it: any, i: number) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.product_name}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.unit}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.quantity}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.warehouse_name}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{fmt(it.unit_price)}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>{fmt(it.total_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button onClick={() => {
              const w = window.open("", "_blank");
              if (w) {
                w.document.write(`<html dir="rtl"><head><title>${viewItem.serial}</title><style>body{font-family:'Vazirmatn',sans-serif;padding:20px;direction:rtl}table{width:100%;border-collapse:collapse}th,td{padding:8px 12px;border:1px solid #ddd;text-align:right;font-size:12px}th{background:#f8fafc;font-weight:700}</style></head><body>${document.getElementById("print-purchase")?.innerHTML || ""}</body></html>`);
                w.document.close(); w.print();
              }
            }} style={addBtnSt("#8b5cf6")}>
              <Printer size={14} /> چاپ سند
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---- shared helpers ----
export const inputSt = (T: any, extra?: any): React.CSSProperties => ({
  width: "100%", padding: "10px 14px", borderRadius: 10,
  border: `1.5px solid ${T.border}`, background: T.input,
  color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  boxSizing: "border-box", outline: "none", ...extra,
});
export const rowSt = (dark: boolean, T: any): React.CSSProperties => ({
  borderRadius: 12, padding: "14px 16px",
  background: dark ? "rgba(255,255,255,0.03)" : "#fff",
  border: `1px solid ${T.cardBorder}`,
});
export const badgeSt = (color: string): React.CSSProperties => ({
  padding: "3px 10px", borderRadius: 8,
  background: `${color}18`, color, fontSize: 12, fontWeight: 700, fontFamily: "monospace",
});
export const addBtnSt = (color: string, small?: boolean): React.CSSProperties => ({
  display: "flex", alignItems: "center", gap: 6,
  padding: small ? "8px 14px" : "10px 20px", borderRadius: 10, border: "none",
  background: `linear-gradient(135deg,${color},${color}bb)`, color: "#fff",
  cursor: "pointer", fontSize: small ? 12 : 13, fontWeight: 700,
  fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
});
export const iconBtnSt = (color: string): React.CSSProperties => ({
  width: 30, height: 30, borderRadius: 8, border: "none",
  background: `${color}18`, color, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
});
export const Lbl = ({ T, children }: any) => (
  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.subtext, marginBottom: 5 }}>
    {children}
  </label>
);
export const BtnCancel = ({ onClick, T }: any) => (
  <button onClick={onClick} style={{
    padding: "10px 20px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`,
    background: "transparent", color: T.text, cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  }}>انصراف</button>
);
export const BtnSave = ({ onClick, loading, color, label }: any) => (
  <button onClick={onClick} disabled={loading} style={{
    padding: "10px 24px", borderRadius: 10, border: "none",
    background: loading ? "#9ca3af" : `linear-gradient(135deg,${color},${color}bb)`,
    color: "#fff", cursor: loading ? "not-allowed" : "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  }}>{loading ? "در حال ثبت..." : label}</button>
);
export const Toast = ({ text, ok }: any) => (
  <div style={{
    position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
    background: ok ? "#10b981" : "#ef4444", color: "#fff",
    padding: "12px 28px", borderRadius: 12, fontSize: 13, fontWeight: 700,
    zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
  }}>{text}</div>
);
export const Loading = ({ T }: any) => (
  <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>
);
export const Empty = ({ T, text }: any) => (
  <div style={{ textAlign: "center", padding: 40, color: T.subtext, background: "rgba(0,0,0,0.02)", borderRadius: 12 }}>
    {text}
  </div>
);
export const Modal = ({ title, onClose, dark, T, children }: any) => (
  <div style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
  }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div style={{
      background: dark ? "#1e293b" : "#fff", borderRadius: 20, padding: "40px",
      width: "100%", maxWidth: 1100, maxHeight: "90vh", overflow: "auto",
      boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.text }}>{title}</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext }}><X size={22} /></button>
      </div>
      {children}
    </div>
  </div>
);